import React, { useState, useEffect, useCallback } from "react";
import { Pagination, Stack } from "@mui/material";
import AddBusinessIcon from "@mui/icons-material/AddBusiness";

import PageContainer from "../layouts/PageContainer";
import CardGridContainer from "../layouts/CardGridContainer";
import PageToolbar from "../components/common/PageToolbar";
import AppButton from "../components/buttons/AppButton";
import ConfirmDialog from "../components/common/ConfirmDialog";

import SupplierCard from "../components/suppliers/SupplierCard";
import CreateSupplierModal from "../components/suppliers/CreateSupplierModal";
import UpdateSupplierModal from "../components/suppliers/UpdateSupplierModal";
import ViewSupplierModal from "../components/suppliers/ViewSupplierModal";

import useDebounce from "../hooks/useDebounce";
import { usePermissions } from "../hooks/usePermissions";
import { useAppDispatch, useAppSelector } from "../redux/store";
import {
  getSuppliers,
  getSupplierById,
  deleteSupplier,
  resetSupplierServerStatus,
} from "../redux/actions/supplierActions";
import { SupplierSummaryDto } from "../shared/dtos/supplier/SupplierSummaryDto";
import { SupplierResponseDto } from "../shared/dtos/supplier/SupplierResponseDto";

const SuppliersPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const { hasPermission } = usePermissions();

  const {
    items: suppliers,
    item: supplier,
    pagination,
    loadingFetchAll: loadingSuppliers,
    loadingFetchOne: loadingSupplier,
    errorFetchAll: errorLoadingSuppliers,
    errorFetchOne: errorLoadingSupplier,
    serverStatus,
    loadingCreate: loadingSupplierCreate,
    errorCreate: errorCreatingSupplier,
    loadingUpdate: loadingSupplierUpdate,
    errorUpdate: errorUpdatingSupplier,
    loadingDelete: loadingSupplierDelete,
  } = useAppSelector((state) => state.supplier);

  // Search & Pagination State
  const [searchTerm, setSearchTerm] = useState<string>("");
  const debouncedSearch = useDebounce(searchTerm, 500);
  const [pageConfig, setPageConfig] = useState({ page: 1, limit: 12 });

  // Modal State
  const [isCreateModalOpen, setIsCreateModalOpen] = useState<boolean>(false);
  const [isUpdateModalOpen, setIsUpdateModalOpen] = useState<boolean>(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState<boolean>(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState<boolean>(false);
  const [selectedSupplier, setSelectedSupplier] = useState<
    SupplierSummaryDto | SupplierResponseDto | null
  >(null);

  // Permissions
  const canCreate = hasPermission("supplier:create");
  const canUpdate = hasPermission("supplier:update");
  const canDelete = hasPermission("supplier:delete");
  const canRead = hasPermission("supplier:read");

  const fetchSuppliersList = useCallback((): void => {
    dispatch(
      getSuppliers({
        page: pageConfig.page,
        size: pageConfig.limit,
        search: debouncedSearch.trim() || undefined,
      }),
    );
  }, [dispatch, pageConfig.page, pageConfig.limit, debouncedSearch]);

  useEffect(() => {
    fetchSuppliersList();
  }, [fetchSuppliersList]);

  // Modal Handlers
  const handleOpenCreate = (): void => setIsCreateModalOpen(true);
  const handleCloseCreate = useCallback((): void => {
    if (!loadingSupplierCreate) setIsCreateModalOpen(false);
  }, [loadingSupplierCreate]);

  const handleOpenUpdate = (
    item: SupplierSummaryDto | SupplierResponseDto,
  ): void => {
    dispatch(getSupplierById(item.id));
    setSelectedSupplier(item);
    setIsUpdateModalOpen(true);
  };
  const handleCloseUpdate = useCallback((): void => {
    if (!loadingSupplierUpdate) {
      setIsUpdateModalOpen(false);
      setSelectedSupplier(null);
    }
  }, [loadingSupplierUpdate]);

  const handleOpenDelete = (
    item: SupplierSummaryDto | SupplierResponseDto,
  ): void => {
    setSelectedSupplier(item);
    setIsDeleteModalOpen(true);
  };
  const handleCloseDelete = useCallback((): void => {
    if (!loadingSupplierDelete) {
      setIsDeleteModalOpen(false);
      setSelectedSupplier(null);
    }
  }, [loadingSupplierDelete]);

  const handleOpenView = (
    item: SupplierSummaryDto | SupplierResponseDto,
  ): void => {
    dispatch(getSupplierById(item.id));
    setIsViewModalOpen(true);
  };
  const handleCloseView = (): void => setIsViewModalOpen(false);

  const handleDeleteConfirm = (): void => {
    if (selectedSupplier) {
      dispatch(deleteSupplier(selectedSupplier.id));
    }
  };

  // Auto-close on success
  useEffect(() => {
    if (!serverStatus) return;
    const { operation, status } = serverStatus;

    if (operation === "create" && status === 201) handleCloseCreate();
    if (operation === "update" && status === 200) handleCloseUpdate();
    if (operation === "delete" && status === 200) handleCloseDelete();

    dispatch(resetSupplierServerStatus());
  }, [
    serverStatus,
    dispatch,
    handleCloseCreate,
    handleCloseUpdate,
    handleCloseDelete,
  ]);

  return (
    <PageContainer
      title="Suppliers"
      subtitle="Manage vendor profiles, contact details, and procurement sources."
      actions={
        canCreate && (
          <AppButton
            variant="contained"
            startIcon={<AddBusinessIcon />}
            onClick={handleOpenCreate}
          >
            Add Supplier
          </AppButton>
        )
      }
    >
      <PageToolbar
        searchTerm={searchTerm}
        onSearchChange={(e) => {
          setSearchTerm(e.target.value);
          setPageConfig((prev) => ({ ...prev, page: 1 }));
        }}
        searchPlaceholder="Search suppliers..."
      />

      <CardGridContainer
        loading={loadingSuppliers}
        error={errorLoadingSuppliers}
        itemCount={suppliers?.length || 0}
        emptyMessage="No Suppliers Found"
        onRetry={fetchSuppliersList}
      >
        {suppliers.map((sup) => (
          <SupplierCard
            key={sup.id}
            supplier={sup}
            onView={canRead ? () => handleOpenView(sup) : undefined}
            onEdit={canUpdate ? () => handleOpenUpdate(sup) : undefined}
            onDelete={canDelete ? () => handleOpenDelete(sup) : undefined}
          />
        ))}
      </CardGridContainer>

      {pagination?.totalPages && pagination.totalPages > 1 && (
        <Stack sx={{ alignItems: "center", mt: 4 }}>
          <Pagination
            count={pagination.totalPages}
            page={pageConfig.page}
            onChange={(_, value) =>
              setPageConfig((prev) => ({ ...prev, page: value }))
            }
            color="primary"
            showFirstButton
            showLastButton
          />
        </Stack>
      )}

      {/* CREATE MODAL */}
      {isCreateModalOpen && (
        <CreateSupplierModal
          open={isCreateModalOpen}
          onClose={handleCloseCreate}
          loading={loadingSupplierCreate}
          error={errorCreatingSupplier}
        />
      )}

      {/* UPDATE MODAL */}
      {isUpdateModalOpen && selectedSupplier && (
        <UpdateSupplierModal
          open={isUpdateModalOpen}
          onClose={handleCloseUpdate}
          supplier={
            (supplier?.id === selectedSupplier.id
              ? supplier
              : selectedSupplier) as SupplierResponseDto
          }
          loading={loadingSupplierUpdate || loadingSupplier}
          error={errorUpdatingSupplier || errorLoadingSupplier}
        />
      )}

      {/* VIEW MODAL */}
      {isViewModalOpen && (
        <ViewSupplierModal
          open={isViewModalOpen}
          onClose={handleCloseView}
          supplier={supplier}
          loading={loadingSupplier}
          error={errorLoadingSupplier}
        />
      )}

      {/* DELETE CONFIRM DIALOG */}
      {isDeleteModalOpen && selectedSupplier && (
        <ConfirmDialog
          open={isDeleteModalOpen}
          onClose={handleCloseDelete}
          onConfirm={handleDeleteConfirm}
          title="Delete Supplier"
          description={`Are you sure you want to delete "${selectedSupplier.name}"? This action cannot be undone.`}
          loading={loadingSupplierDelete}
        />
      )}
    </PageContainer>
  );
};

export default SuppliersPage;
